import {
  WebhookSubscriptionOwnerKindEnum,
  WebhookSubscriptionStatusEnum,
  type WebhookSubscription as GQLItem,
} from '../../../generated/api.graphql';
import { take } from '../../../util';

export type WebhookSubscriptionOwner = {
  kind: WebhookSubscriptionOwnerKindEnum;
  name: string | null;
  present: boolean;
};

export type WebhookSubscription = {
  id: number;
  destinationUrl: string;
  topics: string[];
  includeHolds: boolean;
  status: WebhookSubscriptionStatusEnum;
  delivering: boolean;
  autoPauseReason: string | null;
  autoPausedAt: string | null;
  canEdit: boolean;
  canTakeOwnership: boolean;
  owner: WebhookSubscriptionOwner;
  previousSecretExpiresAt: string | null;
};

// Mirrors the backend's `Webhooks::TopicRegistry`, which no query exposes.
export const TOPICS = ['event.updated', 'event.published', 'event.unpublished', 'event.cancelled', 'order.completed', 'order.refunded'] as const;

export type Topic = typeof TOPICS[number];

export type StopCause = 'switched_off' | 'paused' | 'feature_off' | 'owner_lost';

export const fromGql = (item: GQLItem): WebhookSubscription => ({
  ...take<WebhookSubscription>(item, 'id', 'destinationUrl', 'topics', 'includeHolds', 'status', 'delivering', 'canEdit', 'canTakeOwnership'),
  autoPauseReason: item.autoPauseReason ?? null,
  autoPausedAt: item.autoPausedAt ?? null,
  owner: { kind: item.owner.kind, name: item.owner.name ?? null, present: item.owner.present },
  previousSecretExpiresAt: item.previousSecretExpiresAt ?? null,
});

export const needsNewOwner = (subscription: WebhookSubscription): boolean =>
  subscription.status === WebhookSubscriptionStatusEnum.Suspended && !subscription.owner.present;

/** Why a subscription is not delivering, mirroring the backend's `WebhookSubscription#deliverable?`. */
export const stopCause = (subscription: WebhookSubscription, featureOn: boolean): StopCause | null => {
  if (subscription.delivering) {
    return null;
  }
  // Both need an operator to switch them back on, whatever else is true.
  if (subscription.status === WebhookSubscriptionStatusEnum.Disabled) {
    return 'switched_off';
  }
  if (subscription.status === WebhookSubscriptionStatusEnum.Paused) {
    return 'paused';
  }
  if (!featureOn) {
    return 'feature_off';
  }

  return 'owner_lost';
};
